import * as Sentry from '@sentry/react';
import { log } from '@/lib/logger';

/**
 * Detect mobile device for Sentry config (runs before React, no hooks).
 * Checks both viewport width and touch capability to catch phones
 * in both portrait and landscape orientations. A desktop with a small
 * window still has a fine pointer (mouse), so it won't be misidentified.
 */
function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  const isSmallScreen = window.innerWidth < 768;
  const isTouchPrimary = window.matchMedia('(pointer: coarse)').matches;
  return isSmallScreen || (isTouchPrimary && Math.min(window.innerWidth, window.innerHeight) < 768);
}

export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn || import.meta.env.DEV) {
    log.info('Sentry disabled in development');
    return;
  }

  const mobile = isMobileDevice();

  const integrations: Array<ReturnType<typeof Sentry.browserTracingIntegration> | ReturnType<typeof Sentry.replayIntegration>> = [
    Sentry.browserTracingIntegration(),
  ];

  // Sentry Replay records the full DOM and intercepts all mutations.
  // On memory-constrained mobile devices (especially iOS Safari with 2-3GB RAM),
  // this causes tabs to be killed by the OS, which Safari auto-reloads,
  // creating a reload loop. Only enable on desktop.
  if (!mobile) {
    integrations.push(
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true }),
    );
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_APP_RELEASE || 'development',
    initialScope: {
      tags: { runtime: 'browser' },
    },
    integrations,

    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1),
    // Session replay disabled by default — records every sampled session and
    // exhausts the replay quota fast. Error-triggered replays give the same
    // debugging value for real bugs at a fraction of the volume.
    replaysSessionSampleRate: mobile ? 0 : Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_RATE ?? 0),
    replaysOnErrorSampleRate: mobile ? 0 : Number(import.meta.env.VITE_SENTRY_REPLAYS_ERROR_RATE ?? 1.0),

    beforeSend(event) {
      if (window.location.hostname === 'localhost') {
        return null;
      }

      // Sanitize sensitive data
      if (event.request?.headers) {
        const headers = event.request.headers as Record<string, string>;
        delete headers['Authorization'];
        delete headers['authorization'];
      }

      return event;
    },

    ignoreErrors: [
      'Non-Error promise rejection captured',
      'NetworkError',
      'Failed to fetch',
      // Browser engine noise — not actionable
      'ResizeObserver loop',
      'ResizeObserver loop completed with undelivered notifications',
      /Loading chunk [\d]+ failed/,
      'ChunkLoadError',
      // Our own handler logs this before auto-reloading — not actionable
      'Chunk load error detected',
      /Vite preload error/,
      /Failed to fetch dynamically imported module/,
      // Extensions and third-party script noise
      /^Non-Error exception captured/,
      // Safari-specific
      'cancelled',
      // Chrome extensions injecting errors
      /chrome-extension:\/\//,
    ] });
};
